import React, { useState } from "react";
import PropTypes from "prop-types";
import { useSelector } from "react-redux";
import { Typography } from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

import messages from "../../../../../assets/locale/messages";
import { editEvent } from "../../../../../network/apis/events";
import { getPortalApiErrorMessage } from "../../../../../utils/companyPortal/eventApi";
import { showErrorMsg, showSuccessMsg } from "../../../../../utils/Helpers";
import { UIActionModal, UICallout, UINumberField } from "../../../../ui";
import {
  EVENT_MODELS,
  fillTemplate,
  formatAmount,
  formatCrewSizePrice,
} from "../../../Form/Steps/ChooseCrewSize/crewSizeUtils";
import SummaryRow from "../../../Form/Steps/PublishAndPay/SummaryRow";
import {
  CUSTOM_PLAN_UPGRADE_ERRORS,
  getCustomPlanUpgradeState,
  getCustomPlanUpgradeSummary,
} from "../../CrewSize/customPlanUpgradeUtils";

import "../../../Form/Steps/PublishAndPay/PublishAndPay.scss";
import "../_CrewSizeUpgradeModals.scss";
import "./UpdateCustomPlanModal.scss";

/**
 * Super Admin raises the custom crew size (and rate) of a published event; it applies at
 * once through the event update (PROLINE-4107).
 */
const UpdateCustomPlanModal = ({ open, event, currentPlan, onClose, onUpdated }) => {
  const lang = useSelector((state) => state.locale.lang);
  const { events, shared } = messages[lang];
  const t = events.viewEvents.crewSizeTab.updateCustomPlan;
  const customT = events.creatEvent.crewSize.custom;
  const currency = events.creatEvent.crewSize.currency;

  // Pre-filled with the current plan; the new ceiling must go above it.
  const [plan, setPlan] = useState({
    applicants: String(currentPlan.max),
    rate: String(currentPlan.rate),
  });
  const [touched, setTouched] = useState(false);
  const [saving, setSaving] = useState(false);

  const state = getCustomPlanUpgradeState(plan, currentPlan.max, currentPlan.rate);
  const summary = getCustomPlanUpgradeSummary({
    event,
    currentRate: currentPlan.rate,
    newRate: Number(plan.rate) || 0,
  });
  const currentLabel = fillTemplate(t.customizedUpTo, {
    max: formatAmount(currentPlan.max),
  });
  const errorMessages = {
    [CUSTOM_PLAN_UPGRADE_ERRORS.required]: customT.required,
    [CUSTOM_PLAN_UPGRADE_ERRORS.invalidRate]: customT.invalidRate,
    [CUSTOM_PLAN_UPGRADE_ERRORS.notAboveCurrent]: fillTemplate(t.notAboveCurrent, {
      max: formatAmount(currentPlan.max),
    }),
    [CUSTOM_PLAN_UPGRADE_ERRORS.belowCurrentRate]: fillTemplate(t.rateBelowCurrent, {
      rate: formatAmount(currentPlan.rate),
    }),
  };
  const change = (patch) => {
    setTouched(true);
    setPlan((current) => ({ ...current, ...patch }));
  };
  // The pre-filled ceiling equals the current one; don't flag it before the admin types.
  const applicantsError = touched ? state.applicantsError : null;

  // Only this popup sends the crew size fields of a published event.
  const confirm = async () => {
    setSaving(true);
    try {
      const response = await editEvent(
        {
          id: event.id,
          data: {
            custom_crew_size: Number(plan.applicants),
            custom_rate: Number(plan.rate),
          },
        },
        // The error is toasted once below, not also by the interceptor.
        { handlerEnabled: false },
      );
      // The backend's message, or ours while the update returns none.
      const message = response?.data?.message;
      showSuccessMsg(message || "crewSizeUpgradedSuccessfully", Boolean(message));
      onUpdated(response?.data?.data);
    } catch (error) {
      // The backend's reason: the size isn't bigger, the rate is lower, ...
      showErrorMsg(getPortalApiErrorMessage(error, shared.generalErrMsg));
      setSaving(false);
    }
  };

  return (
    <UIActionModal
      open={open}
      variant="info"
      className="crew-size-upgrade-modal"
      title={t.title}
      onClose={() => !saving && onClose()}
      actions={
        <UIActionModal.Actions
          cancelLabel={shared.cancel}
          confirmLabel={t.confirm}
          confirmDisabled={!state.isValid || saving}
          onCancel={() => !saving && onClose()}
          onConfirm={confirm}
        />
      }
    >
      <UIActionModal.Divider />
      <div className="crew-size-upgrade__body">
        <Typography component="p" className="text-body-sm crew-size-upgrade__message">
          {t.description}
        </Typography>
        <Typography component="p" className="text-label-md crew-size-upgrade__current">
          {t.current} <strong>{currentLabel}</strong>
        </Typography>

        <section className="update-custom-plan__fields">
          <Typography component="h3" className="text-heading-sm update-custom-plan__title">
            {customT.title}
          </Typography>
          <Typography component="p" className="text-body-sm update-custom-plan__subtitle">
            {t.fieldsHint}
          </Typography>
          <div className="update-custom-plan__inputs">
            <UINumberField
              id="update-custom-plan-applicants"
              name="update_custom_plan_applicants"
              label={customT.applicants}
              required
              min={currentPlan.max + 1}
              step={1}
              placeholder={formatAmount(currentPlan.max + 1)}
              value={plan.applicants}
              onValueChange={(applicants) => change({ applicants })}
              error={Boolean(applicantsError)}
              helperText={errorMessages[applicantsError]}
            />
            <UINumberField
              id="update-custom-plan-rate"
              name="update_custom_plan_rate"
              label={summary.extended ? customT.eventRate : customT.ratePerDay}
              required
              min={0}
              placeholder="0"
              value={plan.rate}
              onValueChange={(rate) => change({ rate })}
              error={Boolean(state.rateError)}
              helperText={errorMessages[state.rateError]}
            />
          </div>
        </section>

        <div className="crew-size-upgrade__summary">
          <SummaryRow
            label={t.newCeiling}
            value={fillTemplate(t.newCeilingValue, {
              max: formatAmount(Number(plan.applicants) || 0),
              current: formatAmount(currentPlan.max),
            })}
          />
          {!summary.extended && summary.remainingDays != null && (
            <SummaryRow
              label={t.remainingDays}
              value={fillTemplate(t.remainingDaysValue, {
                remaining: formatAmount(summary.remainingDays),
                total: formatAmount(summary.totalDays),
              })}
            />
          )}
          <SummaryRow
            label={t.rate}
            value={formatCrewSizePrice(
              events.creatEvent.crewSize,
              Number(plan.rate) || 0,
              summary.extended ? EVENT_MODELS.extended : EVENT_MODELS.daily,
              events.creatEvent.crewSize.price.customPlanName,
            )}
          />
          <SummaryRow
            total
            label={t.difference}
            value={
              summary.difference == null
                ? null
                : `${formatAmount(summary.difference)} ${currency}`
            }
          />
        </div>

        <UICallout
          variant="primary"
          hasBorder={false}
          className="update-custom-plan__notice"
          icon={<InfoOutlinedIcon fontSize="small" />}
          title={t.noticeTitle}
          description={t.noticeMessage}
        />
      </div>
    </UIActionModal>
  );
};

UpdateCustomPlanModal.propTypes = {
  open: PropTypes.bool.isRequired,
  /** The published event: its id, `payment_plan` and `remaining_operating_days`. */
  event: PropTypes.object.isRequired,
  /** Resolved custom crew size of the event: its ceiling and rate. */
  currentPlan: PropTypes.shape({
    max: PropTypes.number.isRequired,
    rate: PropTypes.number.isRequired,
  }).isRequired,
  onClose: PropTypes.func.isRequired,
  /** Called with the event the backend returns once the update is saved. */
  onUpdated: PropTypes.func.isRequired,
};

export default UpdateCustomPlanModal;
